import { useCallback } from 'react';

import { cfetch } from '@confluence/network';

/**
 * A whole-suggestion decision recorded against the Space Organizer Inbox.
 *
 * Drop 1 limitation: the endpoint only supports whole-suggestion decisions, so
 * exactly one of the two lists is non-empty per request — approve populates
 * `acceptedActionStepIds`, reject populates `declinedActionStepIds`. The two
 * must never be mixed in the same request.
 */
export type SuggestionDecision = {
	acceptedActionStepIds: string[];
	declinedActionStepIds: string[];
};

export type UseRecordSuggestionDecisionResult = {
	recordDecision: (suggestionId: string, decision: SuggestionDecision) => Promise<void>;
};

const buildDecisionsUrl = (spaceKey: string, suggestionId: string): string =>
	`/wiki/rest/internal/1.0/spaces/${spaceKey}/organization/suggestions/${suggestionId}/decisions`;

/**
 * Records an approve/reject decision for a single suggestion.
 *
 * A successful decision returns `204 No Content` (no body), so we treat any
 * other status as an error and never attempt to parse a response body.
 */
export const recordSuggestionDecision = async (
	spaceKey: string,
	suggestionId: string,
	decision: SuggestionDecision,
): Promise<void> => {
	const response = await cfetch(buildDecisionsUrl(spaceKey, suggestionId), {
		method: 'POST',
		credentials: 'same-origin',
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(decision),
	});

	if (response.status !== 204) {
		throw new Error(`Failed to record suggestion decision: ${response.status}`);
	}
};

/**
 * Returns a stable `recordDecision` bound to the given `spaceKey`. The UI is
 * optimistic (the card is removed before the call resolves), so there is no
 * loading/error state to expose here — callers own the optimistic UX.
 */
export const useRecordSuggestionDecision = (
	spaceKey: string,
): UseRecordSuggestionDecisionResult => {
	const recordDecision = useCallback(
		(suggestionId: string, decision: SuggestionDecision) =>
			recordSuggestionDecision(spaceKey, suggestionId, decision),
		[spaceKey],
	);

	return { recordDecision };
};
